/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

import { weightRepository } from '@/services/repositories/weight.repository.js'

/**
 * Weight Seeder
 * Creates dummy weight history data for goats
 */

/**
 * Generate a random weight in kg
 * @param {number} base - base weight
 * @param {number} variance - +/- range around base
 * @returns {number}
 */
function randomWeight(base = 45, variance = 15) {
  const min = base - variance
  const max = base + variance
  const value = min + Math.random() * (max - min)
  // Round to one decimal place
  return Math.round(value * 10) / 10
}

/**
 * Generate a random date in the past (last 90 days)
 * @returns {string} ISO date string
 */
function randomPastDate() {
  const now = Date.now()
  const daysAgo = Math.floor(Math.random() * 90) // 0-90 days ago
  const timestamp = now - (daysAgo * 24 * 60 * 60 * 1000)
  return new Date(timestamp).toISOString()
}

/**
 * Seed weights for a single goat
 * @param {number} goatId
 * @param {number} count - number of weight records to create
 * @returns {Promise<number[]>} array of created weight IDs (if available)
 */
async function seedWeightsForGoat(goatId, count = 5) {
  console.log(`  Seeding ${count} weights for goat ${goatId}...`)
  const createdIds = []

  for (let i = 0; i < count; i++) {
    const value = randomWeight()
    const id = await weightRepository.create({
      goatId,
      value,
      measuredAt: randomPastDate()
    })
    createdIds.push(id)
    console.log(
      `    Created weight: ${value} kg (id: ${id})`
    )
  }

  return createdIds
}

/**
 * Seed weights for a list of goats
 * @param {number[]} goatIds - array of goat IDs to create weights for
 * @param {number} weightsPerGoat - number of weight records per goat
 * @returns {Promise<void>}
 */
export async function seedWeights(goatIds = [], weightsPerGoat = 5) {
  if (!goatIds.length) {
    console.log('️ No goat IDs provided for weight seeding. Skipping weights.')
    return
  }

  console.log(
    `Seeding weights for ${goatIds.length} goats (about ${weightsPerGoat} per goat)...`
  )

  try {
    for (const goatId of goatIds) {
      await seedWeightsForGoat(goatId, weightsPerGoat)
    }

    console.log('Finished seeding weights.')
  } catch (error) {
    console.error('Failed to seed weights:', error)
    throw error
  }
}
